import type { IconWeight } from '@phosphor-icons/react'
import { useId } from 'react'

import type { NavGlyph, NavGlyphName } from './nav-glyphs'
import { NAV_GLYPHS } from './nav-glyphs'

/**
 * Navigation and module glyphs. Scrambl's own monochrome set (nav-glyphs.ts):
 * the outline is the resting state and the fill the selected one, the way
 * macOS and the ChatGPT app mark selection. The keys are the legacy names
 * call sites already pass; point a key at a glyph here rather than drawing
 * per call site.
 */
const navIconMap = {
  'home-outline': 'home',
  'people-outline': 'users',
  'folder-outline': 'folder',
  'checkbox-outline': 'checkSquare',
  'ticket-outline': 'ticket',
  'time-outline': 'clock',
  'call-outline': 'funnel',
  'person-outline': 'user',
  'bulb-outline': 'kanban',
  'document-text-outline': 'document',
  'receipt-outline': 'receipt',
  'chatbubble-outline': 'chat',
  'grid-outline': 'grid',
  'cube-outline': 'cube',
  'megaphone-outline': 'megaphone',
  'settings-outline': 'gear',
  'log-out-outline': 'signOut',
  'bell-outline': 'bell',
  'building-outline': 'buildings',
  'organization-outline': 'tree',
  'deal-outline': 'tag',
  'person-circle-outline': 'userCircle',
  'today-outline': 'layout',
  'trending-up-outline': 'trendUp',
  'chart-bar-outline': 'chartBar',
  'mail-outline': 'envelope',
  'sticky-note-outline': 'note',
  'calendar-outline': 'calendar',
  'link-outline': 'flow',
  'list-outline': 'list',
  'list-bullets-outline': 'list',
  'clipboard-text-outline': 'clipboard',
  'pulse-outline': 'pulse',
  'contract-outline': 'contract',
  'card-outline': 'card',
  'expense-outline': 'wallet',
  'search-outline': 'search',
} as const satisfies Record<string, NavGlyphName>

type IconName = keyof typeof navIconMap

interface LocalIconProps {
  className?: string
  /** `fill` (or `duotone`) is the selected state; anything else the outline. */
  weight?: IconWeight
}

const FILL_WEIGHTS: ReadonlySet<IconWeight> = new Set(['fill', 'duotone'])
const STROKE = {
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
} as const

function NavGlyphSvg({
  glyph,
  filled,
  className,
}: {
  glyph: NavGlyph
  filled: boolean
  className?: string
}) {
  // Each instance owns its knockout mask: the same glyph is inlined many
  // times per page, and a shared id would resolve to whichever copy came
  // first, even one inside a hidden menu.
  const maskId = `nav-glyph-${useId().replace(/:/g, '')}`
  const bodies = (glyph.body ?? []).map(body =>
    typeof body === 'string' ? body : filled ? body.fill : body.outline,
  )
  const details = glyph.detail ?? []
  const strokes = [
    ...(filled ? [] : [...bodies, ...details.map(detail => detail.d)]),
    ...(glyph.extra ?? []),
  ]

  return (
    <svg
      viewBox="0 0 32 32"
      fill="none"
      aria-hidden
      focusable="false"
      className={className}
      data-weight={filled ? 'fill' : 'regular'}
    >
      {filled && details.length > 0 && (
        <mask
          id={maskId}
          maskUnits="userSpaceOnUse"
          x="0"
          y="0"
          width="32"
          height="32"
        >
          <rect width="32" height="32" fill="#fff" />
          {details.map((detail, i) =>
            detail.cut ? (
              <path key={i} d={detail.cut} fill="#000" />
            ) : (
              <path key={i} d={detail.d} {...STROKE} stroke="#000" />
            ),
          )}
        </mask>
      )}
      <g mask={filled && details.length > 0 ? `url(#${maskId})` : undefined}>
        {filled &&
          bodies.map((d, i) => (
            <path key={`b${i}`} d={d} fill="currentColor" {...STROKE} />
          ))}
        {strokes.map((d, i) => (
          <path key={`s${i}`} d={d} {...STROKE} />
        ))}
      </g>
      {glyph.dots?.map(([cx, cy, r], i) => (
        <circle key={`d${i}`} cx={cx} cy={cy} r={r} fill="currentColor" />
      ))}
    </svg>
  )
}

export function createLocalIcon(
  name: IconName,
  defaultWeight: IconWeight = 'regular',
) {
  const glyph: NavGlyph = NAV_GLYPHS[navIconMap[name]]

  return function LocalIcon({
    className,
    weight = defaultWeight,
  }: LocalIconProps) {
    return (
      <NavGlyphSvg
        glyph={glyph}
        filled={FILL_WEIGHTS.has(weight)}
        className={className}
      />
    )
  }
}
